import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import type {
  AgentGiftPurchase,
  AgentGiftPurchaseResponse,
  CreateGiftPurchaseRequest,
  CreateGiftPurchaseResponse,
  StripeProduct,
  StripeProductsRequest,
  StripeProductsResponse
} from '../types/subscription';

/**
 * Get available gift products from Stripe
 * @returns Available gift products
 */
export const getGiftProducts = async (): Promise<StripeProductsResponse> => {
  try {
    const getProductsFunction = httpsCallable<StripeProductsRequest, StripeProductsResponse>(
      functions,
      'getStripeProducts'
    );
    const result = await getProductsFunction({ type: 'gift' });
    return result.data;
  } catch (error) {
    console.error('Error fetching gift products:', error);
    // Rethrow the original error so the Firebase `code` and the server's
    // HttpsError message survive for ErrorService.getUserFriendlyMessage.
    throw error instanceof Error ? error : new Error('Failed to fetch gift products');
  }
};

/**
 * Direct gift purchase options
 */
export interface DirectGiftOptions {
  customerId?: string;
  customerEmail?: string;
  customerFirstName?: string;
  customerLastName?: string;
}

/**
 * Create a gift purchase checkout session
 * When directGift options are provided, the gift is applied immediately to the customer
 * @param product - The Stripe product to purchase
 * @param quantity - Number of gifts (typically 1 for direct gifts)
 * @param options - Direct gift options with customer info
 * @returns Checkout session details
 */
export const createGiftPurchase = async (
  product: StripeProduct,
  quantity: number,
  options?: DirectGiftOptions
): Promise<CreateGiftPurchaseResponse> => {
  try {
    const createGiftFunction = httpsCallable<
      CreateGiftPurchaseRequest,
      CreateGiftPurchaseResponse
    >(functions, 'createGiftPurchase');

    const result = await createGiftFunction({
      giftType: product.giftId as CreateGiftPurchaseRequest['giftType'],
      quantity,
      unitPrice: product.amount,
      productName: product.name,
      customerId: options?.customerId,
      customerEmail: options?.customerEmail,
      customerFirstName: options?.customerFirstName,
      customerLastName: options?.customerLastName,
    });

    return result.data;
  } catch (error) {
    console.error('Error creating gift purchase:', error);
    throw error instanceof Error ? error : new Error('Failed to create gift purchase');
  }
};

/**
 * Get agent's gift purchase history
 * @param agentId - The agent's ID
 * @returns List of gift purchases made by the agent
 */
export const getAgentGiftPurchases = async (agentId: string): Promise<AgentGiftPurchase[]> => {
  try {
    // The callable returns ISO date strings, not Dates - see
    // AgentGiftPurchaseResponse. They are re-hydrated below.
    const getGiftPurchasesFunction = httpsCallable<
      { agentId: string },
      { purchases: AgentGiftPurchaseResponse[] }
    >(functions, 'getAgentGiftPurchases');

    const result = await getGiftPurchasesFunction({ agentId });

    return result.data.purchases.map(purchase => ({
      ...purchase,
      createdAt: new Date(purchase.createdAt),
      appliedAt: purchase.appliedAt ? new Date(purchase.appliedAt) : undefined,
    }));
  } catch (error) {
    console.error('Error fetching agent gift purchases:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch gift purchase history');
  }
};

// Money formatting lives in one place - see utils/formatters.ts.
// Re-exported so existing `from '../../services/giftService'` imports keep working.
export { formatPrice } from '../utils/formatters';
